import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { VehicleName } from 'shared-ui/lib/components/VehicleName';
import { TableColumn } from 'shared-ui/lib/customs/react/generic-table/TableColumn';
import { formatDate } from 'shared-ui/lib/utils/format-date';
import { routesConfig } from '../config/routes.config';
import { PartnerDashboardRemindersQuery } from '../generated/graphql';

type ReminderRow = PartnerDashboardRemindersQuery['partnerDashboardReminders']['list'][0];

const isLateReminder = (date: string): boolean => moment().isSameOrAfter(moment(date));

const ReminderTitleCell = ({ reminder }: { reminder: ReminderRow }): JSX.Element => {
  const isLate = isLateReminder(reminder.date);

  return (
    <span className={isLate ? 'text-red-color font-bold' : 'text-gray-900'}>{reminder.title}</span>
  );
};

const ReminderDateCell = ({ reminder }: { reminder: ReminderRow }): JSX.Element => {
  const isLate = isLateReminder(reminder.date);

  return (
    <div className={isLate ? 'text-red-color' : 'text-gray-color'}>{formatDate(reminder.date)}</div>
  );
};

const ReminderStatusCell = ({ reminder }: { reminder: ReminderRow }): JSX.Element => {
  const { t } = useTranslation('ServiceCallDashboardScene');
  const isLate = isLateReminder(reminder.date);

  return (
    <span className={isLate ? 'text-red-color font-medium' : 'text-gray-color'}>
      {isLate ? t('statusOverdue') : t('statusOnTime')}
    </span>
  );
};

const businessName: TableColumn = {
  field: 'businessName',
  text: 'ServiceCallDashboardScene:businessName',
  sort: true,
};

const vehicle: TableColumn = {
  field: 'vehicleName',
  text: 'ServiceCallDashboardScene:vehicle',
  sort: true,
  default: '-',
  formatter: (data: ReminderRow) => <VehicleName vehicle={data.vehicle} />,
};

const title: TableColumn = {
  field: 'title',
  text: 'ServiceCallDashboardScene:reminderTitle',
  sort: true,
  formatter: (data: ReminderRow) => <ReminderTitleCell reminder={data} />,
};

const date: TableColumn = {
  field: 'date',
  text: 'ServiceCallDashboardScene:dueDate',
  sort: true,
  formatter: (data: ReminderRow) => <ReminderDateCell reminder={data} />,
};

const status: TableColumn = {
  field: 'status',
  text: 'ServiceCallDashboardScene:status',
  sort: true,
  default: '-',
  formatter: (data: ReminderRow) => <ReminderStatusCell reminder={data} />,
};

const linkToVehicle: TableColumn = {
  field: 'vehicleId',
  text: 'General:link',
  formatter: (data: ReminderRow) => (
    <Link
      to={routesConfig.VEHICLES.SHOW.replace(':id', data.vehicleId)}
      className="hover:text-primary text-gray-color"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-5 w-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M14 5l7 7m0 0l-7 7m7-7H3"
        />
      </svg>
    </Link>
  ),
};

export const dashboardReminderColumns = {
  businessName,
  vehicle,
  title,
  date,
  status,
  linkToVehicle,
};
